/**
 * TabNode — the React Flow custom node that renders one page in the graph.
 *
 * Visual: favicon + title (truncated), hostname, optional visit-count badge.
 * A small external-link affordance appears on hover; clicking it opens the
 * canonical URL in a new tab (same destination as double-clicking the node
 * itself, but more discoverable). Selected state mirrors React Flow's own
 * selection model.
 */

import { memo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { ExternalLink } from "lucide-react";

export interface TabNodeData extends Record<string, unknown> {
  title: string;
  canonicalUrl: string;
  faviconUrl: string;
  urlHost: string;
  visitCount: number;
}

function TabNodeInner({ data, selected }: NodeProps) {
  const d = data as TabNodeData;
  const label = d.title?.trim() || d.canonicalUrl;

  return (
    <div
      className={[
        "group relative rounded-md border bg-card shadow-sm transition-all",
        "flex items-center gap-2 px-3 py-2 w-[200px]",
        selected
          ? "border-primary ring-2 ring-primary/30"
          : "border-border hover:border-foreground/30",
      ].join(" ")}
      title={`${d.title || ""}\n${d.canonicalUrl}\n${d.visitCount} visit${d.visitCount === 1 ? "" : "s"} · double-click to open`}
    >
      <Handle
        type="target"
        position={Position.Left}
        className="!w-2 !h-2 !bg-muted-foreground !border-none"
      />
      {d.faviconUrl ? (
        <img
          src={d.faviconUrl}
          alt=""
          width={16}
          height={16}
          className="flex-shrink-0 rounded-sm"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
          }}
        />
      ) : (
        <div className="flex-shrink-0 w-4 h-4 rounded-sm bg-muted" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate text-foreground">
          {label}
        </div>
        <div className="text-[10px] text-muted-foreground truncate">
          {d.urlHost}
        </div>
      </div>
      {d.visitCount > 1 && (
        <span className="flex-shrink-0 text-[10px] font-medium text-muted-foreground bg-muted px-1.5 rounded group-hover:hidden">
          {d.visitCount}
        </span>
      )}

      {/* Hover-revealed open-in-new-tab affordance. nodrag stops React Flow
          from interpreting the click as the start of a node drag; stopping
          propagation prevents the parent's onNodeClick / onNodeDoubleClick
          handlers from also firing. */}
      <a
        href={d.canonicalUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open page in new tab"
        title="Open in new tab"
        className="nodrag opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 -mr-1 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <ExternalLink className="w-3 h-3" />
      </a>

      <Handle
        type="source"
        position={Position.Right}
        className="!w-2 !h-2 !bg-muted-foreground !border-none"
      />
    </div>
  );
}

export const TabNode = memo(TabNodeInner);
